import React, { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { api } from '@/lib/api';

// Shared scheduling picker used by Exports, Alerts and Insights so every surface
// speaks one normalized schedule spec. Weekdays: 0=Mon … 6=Sun.

const MODES = ['hourly', 'daily', 'weekly', 'monthly'];
const WEEKDAYS = [['Mo', 0], ['Tu', 1], ['We', 2], ['Th', 3], ['Fr', 4], ['Sa', 5], ['Su', 6]];
const MONTHS = [['Jan', 1], ['Feb', 2], ['Mar', 3], ['Apr', 4], ['May', 5], ['Jun', 6],
  ['Jul', 7], ['Aug', 8], ['Sep', 9], ['Oct', 10], ['Nov', 11], ['Dec', 12]];
const QUARTERS = { Q1: [1, 2, 3], Q2: [4, 5, 6], Q3: [7, 8, 9], Q4: [10, 11, 12] };
const WEEK_OPTS = [['1st', 1], ['2nd', 2], ['3rd', 3], ['4th', 4], ['5th', 5], ['Last', -1]];

const COMMON_TZS = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

export function defaultScheduleSpec() {
  let tz = 'UTC';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* noop */ }
  return {
    mode: 'daily', run_now: false, timezone: tz, start: null, end: null,
    time: { hour: 8, minute: 0 },
    hourly: { hours: 1, minutes: 0 },
    daily: { weekdays: [] },
    weekly: { interval: 1, weekdays: [0] },
    monthly: { months: [], week: 1, weekdays: [0] },
  };
}

const FULL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function plainEnglishSchedule(spec) {
  if (!spec) return '';
  const tod = spec.time ? `${String(spec.time.hour ?? 8).padStart(2, '0')}:${String(spec.time.minute ?? 0).padStart(2, '0')}` : '';
  const list = (arr, names) => (arr || []).map(i => names[i]).join(', ');
  if (spec.mode === 'hourly') {
    const h = spec.hourly?.hours || 0, m = spec.hourly?.minutes || 0;
    const parts = [h ? `${h} hour${h === 1 ? '' : 's'}` : '', m ? `${m} minute${m === 1 ? '' : 's'}` : ''].filter(Boolean);
    return `Runs every ${parts.join(' ') || '1 hour'}.`;
  }
  if (spec.mode === 'daily') {
    const d = spec.daily?.weekdays || [];
    return d.length ? `Runs every ${list(d, FULL_DAYS)} at ${tod}.` : `Runs every day at ${tod}.`;
  }
  if (spec.mode === 'weekly') {
    const n = spec.weekly?.interval || 1;
    const d = list(spec.weekly?.weekdays, FULL_DAYS) || 'Monday';
    return `Runs ${n > 1 ? `every ${n} weeks` : 'every week'} on ${d} at ${tod}.`;
  }
  if (spec.mode === 'monthly') {
    const wk = (WEEK_OPTS.find(w => w[1] === (spec.monthly?.week ?? 1)) || ['1st'])[0];
    const d = list(spec.monthly?.weekdays, FULL_DAYS) || 'Monday';
    const months = spec.monthly?.months || [];
    const when = months.length ? `of ${list(months, FULL_MONTHS)}` : 'each month';
    return `Runs on the ${wk} ${d} ${when} at ${tod}.`;
  }
  return '';
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`h-8 min-w-[36px] px-2.5 rounded-lg text-xs font-medium border transition-colors ${
      active ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 text-foreground border-border/50 hover:border-primary/50'
    }`}
  >{children}</button>
);

// Combine a Date + "HH:mm" into a seconds-less local ISO string (no tz suffix).
const combineDateTime = (dateObj, tStr) => {
  if (!dateObj) return null;
  const y = dateObj.getFullYear();
  const mo = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}T${tStr || '08:00'}:00`;
};

// Polished calendar + time picker replacing the raw datetime-local input.
const DateTimeField = ({ value, onChange, testid, placeholder }) => {
  const [open, setOpen] = useState(false);
  const dt = value ? new Date(value) : null;
  const timeVal = value ? value.slice(11, 16) : '08:00';
  const valid = dt && !isNaN(dt.getTime());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="w-full h-9 mt-1 flex items-center gap-2 rounded-md border border-border/50 bg-background px-3 text-sm text-left hover:border-primary/50 transition-colors">
          <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className={valid ? 'text-foreground truncate' : 'text-muted-foreground truncate'}>
            {valid ? dt.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : (placeholder || 'Pick date & time')}
          </span>
          {valid && (
            <X className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0 hover:text-foreground"
              data-testid={`${testid}-clear`}
              onClick={(e) => { e.stopPropagation(); onChange(null); }} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={valid ? dt : undefined}
          onSelect={(d) => onChange(combineDateTime(d, timeVal))} initialFocus />
        <div className="border-t border-border/40 p-3 flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Time</Label>
          <Input type="time" value={timeVal}
            onChange={(e) => onChange(combineDateTime(valid ? dt : new Date(), e.target.value))}
            className="h-8 text-sm" data-testid={`${testid}-time`} />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function SchedulePicker({ value, onChange }) {
  const spec = value || defaultScheduleSpec();
  const tzOptions = COMMON_TZS.includes(spec.timezone) ? COMMON_TZS : [spec.timezone, ...COMMON_TZS];

  // Live preview of the next few run times as the cadence is built.
  const [nextRuns, setNextRuns] = useState([]);
  const specKey = JSON.stringify(spec);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.previewSchedule(spec, 4);
        if (!cancelled) setNextRuns(r.next_runs || []);
      } catch { if (!cancelled) setNextRuns([]); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey]);

  const set = (patch) => onChange({ ...spec, ...patch });
  const setSub = (key, patch) => onChange({ ...spec, [key]: { ...(spec[key] || {}), ...patch } });

  const toggleInArray = (key, sub, val) => {
    const cur = (spec[key]?.[sub]) || [];
    const next = cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val];
    setSub(key, { [sub]: next });
  };

  const timeStr = `${String(spec.time?.hour ?? 8).padStart(2, '0')}:${String(spec.time?.minute ?? 0).padStart(2, '0')}`;
  const onTime = (v) => {
    const [h, m] = (v || '08:00').split(':').map(n => parseInt(n, 10) || 0);
    setSub('time', { hour: h, minute: m });
  };

  return (
    <div className="space-y-3" data-testid="schedule-picker">
      {/* Mode tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-muted/30" data-testid="schedule-mode-tabs">
        {MODES.map(m => (
          <button
            key={m}
            type="button"
            onClick={() => set({ mode: m })}
            data-testid={`schedule-mode-${m}`}
            className={`flex-1 h-8 rounded-md text-xs font-medium capitalize transition-colors ${
              spec.mode === m ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >{m}</button>
        ))}
      </div>

      {/* Per-mode controls */}
      {spec.mode === 'hourly' && (
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Run every</Label>
            <div className="flex items-center gap-1.5 mt-1">
              <Input type="number" min={0} max={24} value={spec.hourly?.hours ?? 1}
                onChange={e => setSub('hourly', { hours: Math.max(0, Math.min(24, parseInt(e.target.value || '0', 10))) })}
                className="h-9 w-16 text-sm" data-testid="schedule-hourly-hours" />
              <span className="text-xs text-muted-foreground">h</span>
              <Input type="number" min={0} max={59} value={spec.hourly?.minutes ?? 0}
                onChange={e => setSub('hourly', { minutes: Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))) })}
                className="h-9 w-16 text-sm" data-testid="schedule-hourly-minutes" />
              <span className="text-xs text-muted-foreground">m</span>
            </div>
          </div>
        </div>
      )}

      {spec.mode !== 'hourly' && (
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">At</Label>
            <Input type="time" value={timeStr} onChange={e => onTime(e.target.value)}
              className="h-9 w-32 text-sm mt-1" data-testid="schedule-time" />
          </div>
          {spec.mode === 'weekly' && (
            <div>
              <Label className="text-xs text-muted-foreground">Every N weeks</Label>
              <Input type="number" min={1} max={12} value={spec.weekly?.interval ?? 1}
                onChange={e => setSub('weekly', { interval: Math.max(1, Math.min(12, parseInt(e.target.value || '1', 10))) })}
                className="h-9 w-20 text-sm mt-1" data-testid="schedule-weekly-interval" />
            </div>
          )}
          {spec.mode === 'monthly' && (
            <div>
              <Label className="text-xs text-muted-foreground">In week</Label>
              <Select value={String(spec.monthly?.week ?? 1)} onValueChange={v => setSub('monthly', { week: parseInt(v, 10) })}>
                <SelectTrigger className="h-9 w-24 text-sm mt-1" data-testid="schedule-monthly-week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEK_OPTS.map(([lbl, val]) => <SelectItem key={val} value={String(val)}>{lbl}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Weekday selectors for daily/weekly/monthly */}
      {(spec.mode === 'daily' || spec.mode === 'weekly' || spec.mode === 'monthly') && (
        <div>
          <Label className="text-xs text-muted-foreground">
            {spec.mode === 'daily' ? 'On days (none = every day)' : 'On weekdays'}
          </Label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="schedule-weekdays">
            {WEEKDAYS.map(([lbl, val]) => {
              const key = spec.mode === 'daily' ? 'daily' : spec.mode;
              const active = (spec[key]?.weekdays || []).includes(val);
              return <Chip key={val} active={active} testid={`schedule-weekday-${val}`}
                onClick={() => toggleInArray(key, 'weekdays', val)}>{lbl}</Chip>;
            })}
          </div>
        </div>
      )}

      {/* Month selectors for monthly */}
      {spec.mode === 'monthly' && (
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">In months (none = every month)</Label>
            <div className="flex gap-1">
              {Object.entries(QUARTERS).map(([q, ms]) => (
                <Chip key={q} active={ms.every(m => (spec.monthly?.months || []).includes(m))} testid={`schedule-quarter-${q}`}
                  onClick={() => {
                    const cur = spec.monthly?.months || [];
                    const all = ms.every(m => cur.includes(m));
                    const next = all ? cur.filter(m => !ms.includes(m)) : [...new Set([...cur, ...ms])];
                    setSub('monthly', { months: next });
                  }}>{q}</Chip>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="schedule-months">
            {MONTHS.map(([lbl, val]) => (
              <Chip key={val} active={(spec.monthly?.months || []).includes(val)} testid={`schedule-month-${val}`}
                onClick={() => toggleInArray('monthly', 'months', val)}>{lbl}</Chip>
            ))}
          </div>
        </div>
      )}

      {/* Timezone */}
      <div>
        <Label className="text-xs text-muted-foreground">Timezone</Label>
        <Select value={spec.timezone} onValueChange={v => set({ timezone: v })}>
          <SelectTrigger className="mt-1 h-9 text-sm" data-testid="schedule-timezone"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            {tzOptions.map(tz => <SelectItem key={tz} value={tz} data-testid={`schedule-tz-${tz}`}>{tz.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Start / End window */}
      <div className="flex gap-3">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">Start (optional)</Label>
          <DateTimeField value={spec.start} onChange={(v) => set({ start: v })} testid="schedule-start" placeholder="Starts immediately" />
        </div>
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">End (optional)</Label>
          <DateTimeField value={spec.end} onChange={(v) => set({ end: v })} testid="schedule-end" placeholder="No end date" />
        </div>
      </div>

      {/* Run now */}
      <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="schedule-run-now-label">
        <input type="checkbox" checked={!!spec.run_now} onChange={e => set({ run_now: e.target.checked })}
          className="accent-primary" data-testid="schedule-run-now" />
        <span className="text-muted-foreground">Also run once now</span>
      </label>

      {/* Plain-English summary */}
      <p className="text-xs text-foreground/80 italic" data-testid="schedule-summary">{plainEnglishSchedule(spec)}</p>

      {/* Next-run preview */}
      {nextRuns.length > 0 && (
        <div className="rounded-lg border border-border/40 bg-muted/20 px-3 py-2" data-testid="schedule-next-runs">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-1">Next runs</p>
          <ul className="space-y-0.5">
            {nextRuns.map((r, i) => (
              <li key={i} className="text-xs text-foreground/90" data-testid={`schedule-next-run-${i}`}>
                {new Date(r).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
